import { useState } from 'react';
import { Box, Chip } from '@mui/material';
import { DataManagementConfig } from '@shared/components/data-management';
import { TenantUserTableData, useTenantUsersTableData } from '../hooks/useTenantUsers';
import { RoleChip } from '@shared/components/RoleChip';
import { userInvitationApi } from '@features/user-invitation/api/userInvitationApi';
import { notificationService } from '@shared/components/notifications';

// Factory function to create tenant users config with server-side pagination and search
export const createTenantUsersConfig = (
  tenantId: string
): DataManagementConfig<TenantUserTableData, never, never> & {
  paginationHook: () => {
    currentPage: number;
    pageSize: number;
    onPageChange: (page: number, size: number) => void;
    searchQuery: string;
    onSearchChange: (searchQuery: string) => void;
    queryResult: ReturnType<typeof useTenantUsersTableData>;
  };
} => {
  const usePaginatedQuery = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');

    const queryResult = useTenantUsersTableData(
      tenantId,
      {
        page: currentPage + 1, // Convert from 0-based to 1-based
        limit: pageSize,
      },
      {
        searchQuery: searchQuery || undefined, // Only pass if not empty
      }
    );

    const onPageChange = (page: number, size: number) => {
      setCurrentPage(page);
      setPageSize(size);
    };

    const onSearchChange = (newSearchQuery: string) => {
      setSearchQuery(newSearchQuery);
      setCurrentPage(0); // Reset to first page when searching
    };

    return {
      currentPage,
      pageSize,
      onPageChange,
      searchQuery,
      onSearchChange,
      queryResult,
    };
  };

  return {
    hooks: {
      useQuery: () => {
        // Default hook for compatibility - uses first page with no search
        return useTenantUsersTableData(tenantId, { page: 1, limit: 10 }, {});
      },
      useCreate: () => ({
        mutateAsync: async () => {
          throw new Error('Create operation not supported for tenant users view');
        },
        isPending: false,
      }),
      // No update/delete operations - this is view-only
    },

    table: {
      title: 'Tenant Users',
      columns: [
        {
          label: 'Name',
          key: 'name',
          render: (value: string | string[] | undefined) =>
            typeof value === 'string' ? value || null : null,
        },
        {
          label: 'Email',
          key: 'email',
        },
        {
          label: 'Roles',
          key: 'roles',
          render: (value: string | string[] | undefined) => {
            if (!value || (Array.isArray(value) && value.length === 0)) {
              return <span style={{ color: '#666', fontStyle: 'italic' }}>No roles</span>;
            }

            const roles = Array.isArray(value) ? value : [value];
            const maxDisplay = 3;
            const displayRoles = roles.slice(0, maxDisplay);
            const remainingCount = roles.length - maxDisplay;

            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {displayRoles.map((role, index) => (
                  <RoleChip
                    key={`${role}-${index}`}
                    role={role}
                    size="small"
                    showTooltip={true}
                    short={false}
                  />
                ))}
                {remainingCount > 0 && (
                  <Chip
                    label={`+${remainingCount} more`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            );
          },
        },
        {
          label: 'Institute',
          key: 'instituteNames',
          render: (value: string | string[] | undefined) => {
            const instituteNames = Array.isArray(value) ? value : [];
            if (!instituteNames || instituteNames.length === 0) {
              return null;
            }

            const maxDisplay = 2;
            const displayItems = instituteNames.slice(0, maxDisplay);
            const remainingCount = instituteNames.length - maxDisplay;

            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {displayItems.map((institute, index) => (
                  <Chip
                    key={`${institute}-${index}`}
                    label={institute}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                ))}
                {remainingCount > 0 && (
                  <Chip
                    label={`+${remainingCount} more`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            );
          },
        },
        {
          label: 'Course',
          key: 'courseNames',
          render: (value: string | string[] | undefined) => {
            const courseNames = Array.isArray(value) ? value : [];
            if (!courseNames || courseNames.length === 0) {
              return null;
            }

            const maxDisplay = 2;
            const displayItems = courseNames.slice(0, maxDisplay);
            const remainingCount = courseNames.length - maxDisplay;

            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {displayItems.map((course, index) => (
                  <Chip
                    key={`${course}-${index}`}
                    label={course}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                ))}
                {remainingCount > 0 && (
                  <Chip
                    label={`+${remainingCount} more`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            );
          },
        },
        {
          label: 'Status',
          key: 'status',
          render: (value: string | string[] | undefined) => {
            const status = (typeof value === 'string' ? value : undefined) || 'active';
            const color =
              status === 'active' ? '#4caf50' : status === 'invited' ? '#ff9800' : '#f44336';
            return (
              <span
                style={{
                  color,
                  fontWeight: 'bold',
                  textTransform: 'capitalize',
                }}
              >
                {status}
              </span>
            );
          },
        },
        {
          label: 'Created',
          key: 'createdAt',
          render: (value: string | string[] | undefined) =>
            typeof value === 'string' && value ? new Date(value).toLocaleDateString() : null,
        },
      ],
      emptyMessage: 'No users found for this university',
      errorMessage: 'Failed to load users. Please try refreshing the page.',
    },

    features: {
      search: true, // Enable server-side search
      pagination: true,
      serverSidePagination: true, // Enable server-side pagination
    },

    paginationHook: usePaginatedQuery,

    actions: {
      custom: [
        {
          label: 'Resend Invite',
          onClick: async (user) => {
            try {
              const response = await userInvitationApi.resendInvitation(user.email);
              if (response.data?.success) {
                notificationService.success(`Invitation resent to ${user.email}`);
              } else {
                notificationService.error(
                  response.data?.errorMessage || response.message || 'Failed to resend invitation'
                );
              }
            } catch (error) {
              console.error('Failed to resend invitation:', error);
              notificationService.error('Failed to resend invitation');
            }
          },
          color: 'primary',
          variant: 'text',
          condition: (user) => user.status === 'invited',
        },
      ],
    },
  };
};
